import fm from 'format-message';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import useCheckoutUrlQuery from 'queries/useCheckoutUrlQuery';
import Link from '@activebrands/core-web/components/Link';
import useNotifications from '@activebrands/core-web/hooks/useNotifications';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import overlay from '@activebrands/core-web/libs/overlay';
import { styled } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import ArrowButton from 'components/buttons/ArrowButton';
import StateIcon from 'components/icons/StateIcon';
import ProductCardMini from 'components/product/ProductCardMini';
import Heading from 'components/text/Heading';
import NotificationWrapper from 'components/wrappers/NotificationWrapper';
import { useStyletron } from 'styletron-react';

const Header = styled('div', {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 16px 8px 16px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--color-contrast-low)',
});

const Buttons = styled('div', {
    display: 'flex',
    gap: '8px',
    padding: '0 8px',
});

const buttonStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    textAlign: 'left',
    flex: 1,
    ...textStyles['Miscellaneous/14_100'],
};

const AddedToBasket = ({
    color,
    containImage = false,
    id,
    media,
    name,
    onMouseEnter,
    onMouseLeave,
    price,
    size,
    uri = '',
}) => {
    const [css] = useStyletron();
    const { checkoutPage } = useCheckoutUrlQuery();
    const notifications = useNotifications();

    const closeNotificationOnViewCart = () => {
        overlay.open(OVERLAY_ID.BASKET);
        notifications.remove(id);
    };

    const productLabel = size ? `${name}, size ${size}` : name;

    return (
        <NotificationWrapper
            data-selector="global.minicart"
            role="status"
            aria-live="polite"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <span
                className={css({
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                })}
            >
                {fm('{product} added to cart', { product: productLabel })}
            </span>
            <Header>
                <Heading data-selector="global.minicart.title" fontKeys="Primary/20-120_-1">
                    {fm('Added to cart')}
                </Heading>
                <button
                    data-selector="global.minicart.close"
                    type="button"
                    aria-label={fm('Close notification')}
                    className={css({ cursor: 'pointer', ...getFocusRingStyles() })}
                    onClick={() => notifications.remove(id)}
                >
                    <StateIcon size="14px" state="close" />
                </button>
            </Header>
            <div className={css({ padding: '0 16px', marginBottom: '16px' })}>
                <ProductCardMini
                    selector="global.minicart.item"
                    color={color}
                    containImage={containImage}
                    id={id}
                    media={media}
                    name={name}
                    size={size}
                    uri={uri}
                    price={price}
                    type="order"
                />
            </div>
            <Buttons>
                <ArrowButton
                    data-selector="global.minicart.view-cart"
                    $style={buttonStyles}
                    size="sm"
                    onClick={() => closeNotificationOnViewCart()}
                >
                    {fm('View cart')}
                </ArrowButton>
                <ArrowButton
                    data-selector="global.minicart.checkout"
                    $style={buttonStyles}
                    as={Link}
                    size="sm"
                    theme="inverted"
                    to={checkoutPage}
                    onClick={() => notifications.remove(id)}
                >
                    {fm('Checkout')}
                </ArrowButton>
            </Buttons>
        </NotificationWrapper>
    );
};

AddedToBasket.propTypes = {
    color: PropTypes.string,
    containImage: PropTypes.bool,
    id: PropTypes.string.isRequired,
    media: PropTypes.object,
    name: PropTypes.string.isRequired,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    price: PropTypes.object,
    size: PropTypes.string.isRequired,
    uri: PropTypes.string,
};

export default AddedToBasket;
